import React, { useState, useEffect, useCallback, useRef } from 'react';
import { HoverCard, HoverCardTrigger, HoverCardContent } from './ui/hover-card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Loader2 } from 'lucide-react';
import { getProxyImageUrl } from '../utils';
import EffectText from './EffectText';

// ─── In-memory card cache (shared across all CardCode instances) ──
const cardCache = new Map();

/**
 * CardCode – renders an inline interactive card reference.
 *
 *   <CardCode cardNumber="BP05-044" />                  → shows "BP05-044"
 *   <CardCode cardNumber="BP05-044">Mefilas</CardCode>  → shows "Mefilas"
 *
 * • Hover → HoverCard with thumbnail + basic stats
 * • Click → Full-size Dialog modal with card image and details
 * • Fetches data from /api/cards/number/:number and caches the result.
 */
export default function CardCode({ cardNumber, lang = 'EN', children }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const fetchedRef = useRef(false);

  const fetchCard = useCallback(async () => {
    if (!cardNumber) return;

    // Return cached data instantly
    const cacheKey = `${cardNumber}__${lang}`;
    if (cardCache.has(cacheKey)) {
      setCard(cardCache.get(cacheKey));
      return;
    }

    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);

    try {
      const res = await fetch(
        `/api/cards/number/${encodeURIComponent(cardNumber)}?lang=${lang}`
      );
      if (!res.ok) throw new Error('not found');
      const data = await res.json();
      cardCache.set(cacheKey, data);
      setCard(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [cardNumber, lang]);

  // Pre-fetch when the component mounts (lightweight – uses cache on repeat)
  useEffect(() => {
    fetchCard();
  }, [fetchCard]);

  // Unreleased cards come back from the API as reveals, with their own image.
  const imageUrl = card
    ? getProxyImageUrl(
        card.images?.blob?.full || card.images?.original?.full || card.images?.revealed?.full
      )
    : '';
  const isRevealed = card?.status === 'revealed';

  const formatRarity = (rarity) => {
    if (!rarity) return '';
    const val = typeof rarity === 'object' ? rarity.value : rarity;
    if (!val) return '';
    const parts = String(val).split('_');
    return (parts[parts.length - 1] || '').toUpperCase();
  };

  // ── Popover preview card ──────────────────────────────────
  const PreviewContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (error || !card) {
      return (
        <div className="text-sm text-muted-foreground p-2">Card not found</div>
      );
    }
    // Image-first: the card image is readable at this size, so the hover shows it big
    // with a one-line caption; full stats and effect text are in the click dialog.
    if (imageUrl) {
      return (
        <div className="w-[min(85vw,300px)]">
          <img src={imageUrl} alt={card.name} className="w-full rounded-md shadow-md" loading="lazy" />
          <div className="mt-1.5 flex items-center gap-1.5 px-0.5 text-xs">
            <span className="truncate font-medium">{card.name}</span>
            <span className="font-mono text-muted-foreground">{cardNumber}</span>
            {isRevealed && (
              <Badge className="ml-auto shrink-0 bg-amber-400 px-1.5 py-0 text-[10px] text-black hover:bg-amber-400">
                Revealed
              </Badge>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="flex max-w-xs flex-col gap-1">
        <p className="truncate text-sm font-semibold leading-tight">{card.name}</p>
        {card.characterName && (
          <p className="truncate text-xs text-muted-foreground">{card.characterName}</p>
        )}
        <div className="mt-1 flex flex-wrap gap-1">
          {card.level != null && (
            <Badge variant="outline" className="px-1.5 py-0 text-[10px]">Lv.{card.level}</Badge>
          )}
          {card.rarity && (
            <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">{formatRarity(card.rarity)}</Badge>
          )}
        </div>
        {card.effect && (
          <EffectText text={card.effect} className="mt-1 line-clamp-4 text-[11px] leading-snug text-muted-foreground" />
        )}
      </div>
    );
  };

  return (
    <>
      {/* Inline link with HoverCard preview on hover, click opens full modal */}
      <HoverCard openDelay={150} closeDelay={100}>
        <HoverCardTrigger asChild>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setShowModal(true);
            }}
            className={`inline text-primary underline underline-offset-2 decoration-primary/40 hover:decoration-primary cursor-pointer bg-transparent border-none p-0 m-0 leading-inherit${
              children ? '' : ' font-mono text-[0.92em]'
            }`}
          >
            {children ?? cardNumber}
          </button>
        </HoverCardTrigger>
        <HoverCardContent
          side="top"
          align="start"
          collisionPadding={12}
          className="w-auto max-w-sm p-2"
        >
          <PreviewContent />
        </HoverCardContent>
      </HoverCard>

      {/* Full card modal on click */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{card?.name || cardNumber}</DialogTitle>
          </DialogHeader>
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          {error && (
            <p className="text-center text-muted-foreground py-8">
              Could not load card data for {cardNumber}.
            </p>
          )}
          {card && !loading && (
            <div className="flex flex-col items-center gap-4">
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={card.name}
                  className="w-64 h-auto rounded-lg shadow-md"
                />
              )}
              <div className="w-full space-y-2 text-sm">
                {card.characterName && (
                  <p>
                    <span className="text-muted-foreground">Character:</span>{' '}
                    {card.characterName}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">{cardNumber}</Badge>
                  {card.level != null && (
                    <Badge variant="secondary">Lv.{card.level}</Badge>
                  )}
                  {card.round != null && (
                    <Badge variant="secondary">Round {card.round}</Badge>
                  )}
                  {card.rarity && (
                    <Badge variant="secondary">
                      {formatRarity(card.rarity)}
                    </Badge>
                  )}
                  {card.type && (
                    <Badge variant="outline">
                      {typeof card.type === 'object'
                        ? card.type.value
                        : card.type}
                    </Badge>
                  )}
                </div>
                {card.effect && (
                  <EffectText
                    text={card.effect}
                    className="bg-muted/50 rounded-md p-3 text-sm leading-relaxed"
                  />
                )}
                {isRevealed && card.source?.url && (
                  <a
                    href={card.source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline"
                  >
                    {card.verified === false
                      ? 'Revealed card — unverified text transcribed from the official reveal image.'
                      : 'Revealed card — text from the official reveal; may change at release.'}
                  </a>
                )}
                {card.flavorText && (
                  <div className="bg-muted/30 rounded-md p-3 text-sm italic text-muted-foreground">
                    "{card.flavorText}"
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
